import { useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  fetchMyNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../api/notifications';
import type { UserNotification } from '../types/notifications';

/* ── Type icons (inline SVG) ──────────────────────────────────────────── */

function MegaphoneIcon() {
  return (
    <svg className="h-5 w-5 text-blue-500 dark:text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="h-5 w-5 text-green-500 dark:text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg className="h-5 w-5 text-yellow-500 dark:text-yellow-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0016.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.016 6.016 0 01-4.27 1.772 6.016 6.016 0 01-4.27-1.772" />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg className="h-5 w-5 text-blue-500 dark:text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

function LightbulbIcon() {
  return (
    <svg className="h-5 w-5 text-amber-500 dark:text-amber-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
    </svg>
  );
}

function typeIcon(type: string) {
  switch (type) {
    case 'broadcast': return <MegaphoneIcon />;
    case 'report_resolved': return <CheckIcon />;
    case 'badge_earned': return <TrophyIcon />;
    case 'daily_reminder': return <BookIcon />;
    default: return <LightbulbIcon />;
  }
}

type Translator = (key: string, options?: Record<string, unknown>) => string;

function relativeTime(dateStr: string, t: Translator): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('components.notification.time.justNow');
  if (mins < 60) return t('components.notification.time.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('components.notification.time.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('components.notification.time.daysAgo', { count: days });
  return new Date(dateStr).toLocaleDateString();
}

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
}

export default function NotificationPanel({ open, onClose }: NotificationPanelProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  /* Click-outside to close */
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose]);

  /* Escape key to close */
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => fetchMyNotifications({ limit: 30 }),
    enabled: open,
  });

  const markOneMut = useMutation({
    mutationFn: ({ id, source }: { id: number; source: 'targeted' | 'broadcast' }) =>
      markNotificationRead(id, source),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unread-count'] });
    },
  });

  const markAllMut = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unread-count'] });
    },
  });

  if (!open) return null;

  const items: UserNotification[] = data?.items ?? [];
  const hasUnread = items.some((n) => !n.is_read);

  return (
    <div
      ref={panelRef}
      className="absolute right-0 mt-2 w-80 sm:w-96 rounded-2xl border border-slate-200/70 bg-white shadow-[0_4px_8px_rgba(15,23,42,0.06),0_18px_40px_-12px_rgba(15,23,42,0.20)] overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-150 dark:border-white/10 dark:bg-card-dark"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-white/[0.06]">
        <p className="text-sm font-semibold text-slate-900 dark:text-white">{t('components.notification.panelTitle')}</p>
        {hasUnread && (
          <button
            onClick={() => markAllMut.mutate()}
            disabled={markAllMut.isPending}
            className="text-xs font-medium text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
          >
            {t('components.notification.markAllRead')}
          </button>
        )}
      </div>

      {/* List */}
      <div className="max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
            {t('components.notification.loading')}
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
            {t('components.notification.empty')}
          </div>
        ) : (
          items.map((n) => (
            <button
              key={`${n.source}-${n.id}`}
              onClick={() => {
                if (!n.is_read) markOneMut.mutate({ id: n.id, source: n.source });
              }}
              className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors rounded-xl mx-1 my-0.5 hover:bg-slate-50 dark:hover:bg-white/5 ${
                !n.is_read
                  ? 'border-l-2 border-l-blue-600 bg-blue-50/60 dark:bg-blue-500/10'
                  : 'border-l-2 border-l-transparent'
              }`}
            >
              <div className="mt-0.5">{typeIcon(n.type)}</div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                  {n.title}
                </p>
                {n.message && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">
                    {n.message}
                  </p>
                )}
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  {relativeTime(n.created_at, t)}
                </p>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
